'use client'

import { truncateWithEllipsis } from '@/domain/common/format'
import { NormalizedTokenInfo, getTokenImageUrl } from '@/domain/euler'
import { NATIVE_HYPE_ADDRESS } from '@/domain/euler/nativeHype'
import type { BorrowPosition, CollateralPosition } from '@/domain/euler/rebalance'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets as uiAssets } from '@/ui/assets'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { CheckCircle2 } from 'lucide-react'
import Image from 'next/image'
import { useMemo } from 'react'
import { Address, formatUnits } from 'viem'

import type { PooledUserPosition } from '../hooks/usePooledRoutes'
import { TokenOption, TokenSelectorDialog } from './TokenSelectorDialog'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'

// Types shared with container
export type SortMode = 'Best' | 'Lowest borrow APY' | 'Highest max LTV' | 'Most liquidity'
export type RouteDirection = 'long' | 'short' | 'neutral'
export type RouteType = 'euler' | 'pooled'

export interface BorrowAssetOption {
  underlying: Address
  symbol: string
  name?: string
  logoURI?: string
  tokenInfo?: NormalizedTokenInfo
  liquidityUsdHint?: number
  /** User's balance of this token */
  balance?: string
  /** Price per token in USD */
  priceUsd?: number
}

export interface ClusterMarketRow {
  underlying: Address
  vault: Address
  symbol: string
  name?: string
  tokenInfo?: NormalizedTokenInfo
  supplyAPY?: number
  borrowAPY?: number
  liquidityUSD?: number
}

export interface RouteInfo {
  collateral: Address
  liability: Address
  collateralSymbol?: string
  liabilitySymbol?: string
  collateralTokenInfo?: NormalizedTokenInfo
  liabilityTokenInfo?: NormalizedTokenInfo
  borrowAPY?: number
  maxLTV?: bigint
  liquidityUSD?: number
  direction: RouteDirection
  hasPosition?: boolean
  clusterName?: string
  /** Route source type: 'euler' for Mewler/EVC routes, 'pooled' for HypurrFi Pooled (Aave) */
  routeType: RouteType
  // User position data
  userCollateralDeposit?: number
  userCollateralDepositUsd?: number
  userBorrowAmount?: number
  userBorrowAmountUsd?: number
  /** Collateral asset price in USD (used for pooled routes) */
  collateralPriceUsd?: number
  /** Borrow asset price in USD (used for pooled routes) */
  borrowPriceUsd?: number
  /** Liquidation LTV in basis points (used for pooled routes) */
  liquidationLTV?: bigint
  /** Utilization rate (0-100 percentage) */
  utilization?: number
  /** Health factor for positions with debt (pooled markets have global HF) */
  healthFactor?: number
}

interface MarketExplorerProps {
  collateralOptions: BorrowAssetOption[]
  selectedCollateralUnderlying?: Address
  onSelectCollateral: (addr: Address) => void

  borrowOptions: BorrowAssetOption[]
  selectedBorrowUnderlying?: Address
  onSelectBorrow: (addr: Address) => void

  routes: RouteInfo[]
  selectedRouteKey?: string
  onSelectRoute: (key: string) => void

  /** Map of vault address -> logo URI */
  vaultLogos?: Map<string, string | undefined>

  /** Map of route key -> positions matching that route */
  routePositionsMap?: Map<string, RoutePositions[]>

  /** Set of liability vault addresses (lowercase) where user has existing borrow positions */
  liabilitiesWithExistingPositions?: Set<string>

  /** Selected position: subAccountId number for existing, 'new' for create new */
  selectedPositionId?: number | 'new'
  /** Callback when position is selected */
  onSelectPosition?: (positionId: number | 'new') => void

  /** Collateral amount input value */
  collateralAmount?: string
  /** Collateral amount change handler */
  onCollateralAmountChange?: (value: string) => void
  /** Collateral balance for max button */
  collateralBalance?: string
  /** Collateral price in USD */
  collateralPriceUsd?: number

  /** Borrow amount input value */
  borrowAmount?: string
  /** Borrow amount change handler */
  onBorrowAmountChange?: (value: string) => void
  /** Max borrow amount */
  maxBorrowAmount?: string
  /** Borrow price in USD */
  borrowPriceUsd?: number

  // Native HYPE support for collateral
  /** Whether collateral underlying is WHYPE (supports native HYPE) */
  collateralSupportsNative?: boolean
  /** Whether to use native HYPE for collateral */
  useNativeCollateral?: boolean
  /** Callback when native collateral selection changes */
  onUseNativeCollateralChange?: (useNative: boolean) => void
  /** Native HYPE balance */
  nativeCollateralBalance?: string
  /** Wrapped token balance */
  wrappedCollateralBalance?: string

  // Native HYPE support for borrow
  /** Whether borrow underlying is WHYPE (supports native HYPE unwrap) */
  borrowSupportsNative?: boolean
  /** Whether to receive native HYPE for borrow */
  receiveNativeBorrow?: boolean
  /** Callback when native borrow selection changes */
  onReceiveNativeBorrowChange?: (receiveNative: boolean) => void
  /** If true, disable the borrow amount input (e.g. when in Multiply mode) */
  disableBorrowInput?: boolean
  /** User's complete pooled market position (all supplies and borrows) */
  userPooledPosition?: PooledUserPosition
  /** Whether the route was auto-adjusted due to borrow amount exceeding liquidity */
  routeAutoAdjusted?: boolean
}

export interface RoutePositions {
  subAccountId: number
  collaterals: CollateralPosition[]
  borrows: BorrowPosition[]
  /** If true, this position has a different controller that needs to be disabled first */
  needsControllerSwitch?: boolean
  /** The existing controller address that needs to be disabled (only set if needsControllerSwitch is true) */
  existingController?: Address
}

function formatPercent(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return '-'
  return `${value.toFixed(2)}%`
}

function formatLtv(value: bigint | undefined): string {
  if (value === undefined) return '-'
  return `${(Number(value) / 100).toFixed(0)}%`
}

function formatUsd(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return '-'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

export function MarketExplorer({
  collateralOptions,
  selectedCollateralUnderlying,
  onSelectCollateral,
  borrowOptions,
  selectedBorrowUnderlying,
  onSelectBorrow,
  routes,
  selectedRouteKey,
  onSelectRoute,
  vaultLogos,
  routePositionsMap = new Map(),
  liabilitiesWithExistingPositions = new Set(),
  selectedPositionId,
  onSelectPosition,
  collateralAmount,
  onCollateralAmountChange,
  collateralBalance,
  collateralPriceUsd,
  borrowAmount,
  onBorrowAmountChange,
  maxBorrowAmount,
  borrowPriceUsd,
  // Native HYPE support
  collateralSupportsNative,
  useNativeCollateral,
  onUseNativeCollateralChange,
  nativeCollateralBalance,
  wrappedCollateralBalance,
  borrowSupportsNative,
  receiveNativeBorrow,
  onReceiveNativeBorrowChange,
  disableBorrowInput = false,
  userPooledPosition,
  routeAutoAdjusted,
}: MarketExplorerProps) {
  // Build enhanced collateral options: add native HYPE when WHYPE is present
  const enhancedCollateralOptions = useMemo((): TokenOption[] => {
    const options: TokenOption[] = collateralOptions.map((opt) => {
      // Update WHYPE option with wrapped balance
      if (opt.symbol === 'WHYPE' && wrappedCollateralBalance !== undefined) {
        return { ...opt, balance: wrappedCollateralBalance }
      }
      return opt
    })

    // Find WHYPE in options and add native HYPE alongside it
    const whypeOption = collateralOptions.find((opt) => opt.symbol === 'WHYPE')
    if (whypeOption) {
      // Add native HYPE option (use special address to identify it)
      options.unshift({
        underlying: NATIVE_HYPE_ADDRESS,
        symbol: 'HYPE',
        name: 'Hype (Native)',
        tokenInfo: whypeOption.tokenInfo, // Use same logo
        balance: nativeCollateralBalance,
        priceUsd: whypeOption.priceUsd, // Same price as WHYPE
      })
    }

    return options
  }, [collateralOptions, nativeCollateralBalance, wrappedCollateralBalance])

  // Build enhanced borrow options: add native HYPE when WHYPE is present
  const enhancedBorrowOptions = useMemo((): TokenOption[] => {
    const options: TokenOption[] = [...borrowOptions]

    // Find WHYPE in options and add native HYPE alongside it
    const whypeOption = borrowOptions.find((opt) => opt.symbol === 'WHYPE')
    if (whypeOption) {
      // Add native HYPE option
      options.unshift({
        underlying: NATIVE_HYPE_ADDRESS,
        symbol: 'HYPE',
        name: 'Hype (Native)',
        tokenInfo: whypeOption.tokenInfo,
        liquidityUsdHint: whypeOption.liquidityUsdHint,
      })
    }

    return options
  }, [borrowOptions])

  // Handle collateral selection - detect native vs wrapped and sync toggle
  const handleCollateralSelect = (addr: Address) => {
    if (addr === NATIVE_HYPE_ADDRESS) {
      // User selected native HYPE - find WHYPE and select it, toggle to native
      const whypeOption = collateralOptions.find((opt) => opt.symbol === 'WHYPE')
      if (whypeOption) {
        onSelectCollateral(whypeOption.underlying)
        onUseNativeCollateralChange?.(true)
      }
    } else {
      // Normal selection
      onSelectCollateral(addr)
      // If selecting WHYPE directly, set toggle to wrapped
      const selected = collateralOptions.find((opt) => opt.underlying.toLowerCase() === addr.toLowerCase())
      if (selected?.symbol === 'WHYPE') {
        onUseNativeCollateralChange?.(false)
      }
    }
  }

  // Handle borrow selection - detect native vs wrapped and sync toggle
  const handleBorrowSelect = (addr: Address) => {
    if (addr === NATIVE_HYPE_ADDRESS) {
      // User selected native HYPE - find WHYPE and select it, toggle to native
      const whypeOption = borrowOptions.find((opt) => opt.symbol === 'WHYPE')
      if (whypeOption) {
        onSelectBorrow(whypeOption.underlying)
        onReceiveNativeBorrowChange?.(true)
      }
    } else {
      // Normal selection
      onSelectBorrow(addr)
      // If selecting WHYPE directly, set toggle to wrapped
      const selected = borrowOptions.find((opt) => opt.underlying.toLowerCase() === addr.toLowerCase())
      if (selected?.symbol === 'WHYPE') {
        onReceiveNativeBorrowChange?.(false)
      }
    }
  }

  // Determine which underlying to show as selected in dropdown
  // If WHYPE is selected and native toggle is on, show native HYPE as selected
  const displayedCollateralUnderlying = useMemo(() => {
    if (!selectedCollateralUnderlying) return undefined
    const selected = collateralOptions.find(
      (opt) => opt.underlying.toLowerCase() === selectedCollateralUnderlying.toLowerCase(),
    )
    if (selected?.symbol === 'WHYPE' && useNativeCollateral) {
      return NATIVE_HYPE_ADDRESS
    }
    return selectedCollateralUnderlying
  }, [selectedCollateralUnderlying, collateralOptions, useNativeCollateral])

  const displayedBorrowUnderlying = useMemo(() => {
    if (!selectedBorrowUnderlying) return undefined
    const selected = borrowOptions.find(
      (opt) => opt.underlying.toLowerCase() === selectedBorrowUnderlying.toLowerCase(),
    )
    if (selected?.symbol === 'WHYPE' && receiveNativeBorrow) {
      return NATIVE_HYPE_ADDRESS
    }
    return selectedBorrowUnderlying
  }, [selectedBorrowUnderlying, borrowOptions, receiveNativeBorrow])

  // Get the selected collateral and borrow symbols for matching routes
  const selectedCollateralSymbol = useMemo(() => {
    if (!selectedCollateralUnderlying) return undefined
    const option = collateralOptions.find(
      (opt) => opt.underlying.toLowerCase() === selectedCollateralUnderlying.toLowerCase(),
    )
    return option?.symbol
  }, [collateralOptions, selectedCollateralUnderlying])

  const selectedBorrowSymbol = useMemo(() => {
    if (!selectedBorrowUnderlying) return undefined
    const option = borrowOptions.find((opt) => opt.underlying.toLowerCase() === selectedBorrowUnderlying.toLowerCase())
    return option?.symbol
  }, [borrowOptions, selectedBorrowUnderlying])

  // Get the borrow APY for the selected route (or best APY if no route selected)
  const selectedBorrowAPY = useMemo(() => {
    // If a route is selected, use its APY
    if (selectedRouteKey) {
      const selectedRoute = routes.find((r) => `${r.collateral}-${r.liability}` === selectedRouteKey)
      if (selectedRoute?.borrowAPY !== undefined) {
        return selectedRoute.borrowAPY
      }
    }

    // Otherwise, show best (lowest) APY from matching routes
    if (!selectedCollateralSymbol || !selectedBorrowSymbol) return undefined

    const matchingRoutes = routes.filter(
      (r) =>
        r.collateralSymbol?.toLowerCase() === selectedCollateralSymbol.toLowerCase() &&
        r.liabilitySymbol?.toLowerCase() === selectedBorrowSymbol.toLowerCase(),
    )

    if (matchingRoutes.length === 0) return undefined

    const apys = matchingRoutes.map((r) => r.borrowAPY).filter((apy): apy is number => apy !== undefined)
    if (apys.length === 0) return undefined

    return Math.min(...apys)
  }, [routes, selectedRouteKey, selectedCollateralSymbol, selectedBorrowSymbol])

  // Calculate USD values
  const collateralUsdValue =
    collateralAmount && collateralPriceUsd ? Number(collateralAmount) * collateralPriceUsd : undefined
  const borrowUsdValue = borrowAmount && borrowPriceUsd ? Number(borrowAmount) * borrowPriceUsd : undefined

  const getLogoForVault = (vaultAddress: Address): string | undefined => {
    return vaultLogos?.get(vaultAddress.toLowerCase())
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Asset Selection Row - Lend and Borrow on same line */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Lend */}
        <div className="flex flex-col gap-2">
          <div className="flex h-8 items-center gap-3">
            <Typography variant="h3" gradient>
              Lend
            </Typography>
            {/* Native HYPE Toggle */}
            {collateralSupportsNative && (
              <div className="flex gap-0.5 rounded border border-border/50 bg-panel/50 p-0.5">
                <button
                  type="button"
                  onClick={() => onUseNativeCollateralChange?.(true)}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] transition-colors',
                    useNativeCollateral
                      ? 'bg-primary/20 font-medium text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  HYPE
                </button>
                <button
                  type="button"
                  onClick={() => onUseNativeCollateralChange?.(false)}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] transition-colors',
                    !useNativeCollateral
                      ? 'bg-primary/20 font-medium text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  WHYPE
                </button>
              </div>
            )}
          </div>
          <div className="flex rounded-lg border border-border bg-panel">
            <TokenSelectorDialog
              options={enhancedCollateralOptions}
              selectedUnderlying={displayedCollateralUnderlying}
              onSelect={handleCollateralSelect}
              title="Select collateral token"
              sortByBalance
              showBalance
            />
            <div className="flex flex-1 items-center justify-between px-4">
              {/* Left: Amount + USD */}
              <div className="flex flex-col">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={collateralAmount ?? ''}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized !== null) {
                      onCollateralAmountChange?.(normalized)
                    }
                  }}
                  className="no-scrollbar w-full overflow-scroll bg-transparent text-white text-xl outline-none placeholder:text-white/30"
                  disabled={!selectedCollateralUnderlying}
                />
                <span className="no-scrollbar max-w-12 overflow-scroll text-muted-foreground text-xs">
                  {USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(collateralUsdValue ?? 0), { compact: true })}
                </span>
              </div>
              {/* Right: MAX above Balance */}
              <div className="flex flex-col items-end pl-1">
                {collateralBalance && (
                  <>
                    <button
                      type="button"
                      onClick={() => onCollateralAmountChange?.(collateralBalance)}
                      className="font-medium text-primary text-xs hover:text-primary/80"
                    >
                      MAX
                    </button>
                    <div className="flex items-center gap-1">
                      <Image src={uiAssets.wallet} alt="wallet" width={12} height={12} />
                      <span className="text-muted-foreground text-xs">
                        {USD_MOCK_TOKEN.format(NormalizedUnitNumber(collateralBalance), { style: 'compact' })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Borrow */}
        <div className="flex flex-col gap-2">
          <div className="flex h-8 items-center justify-between">
            <div className="flex items-center gap-3">
              <Typography variant="h3" gradient>
                Borrow
              </Typography>
              {/* Native HYPE Toggle for receiving */}
              {borrowSupportsNative && (
                <div className="flex gap-0.5 rounded border border-border/50 bg-panel/50 p-0.5">
                  <button
                    type="button"
                    onClick={() => onReceiveNativeBorrowChange?.(true)}
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] transition-colors',
                      receiveNativeBorrow
                        ? 'bg-primary/20 font-medium text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    HYPE
                  </button>
                  <button
                    type="button"
                    onClick={() => onReceiveNativeBorrowChange?.(false)}
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] transition-colors',
                      !receiveNativeBorrow
                        ? 'bg-primary/20 font-medium text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    WHYPE
                  </button>
                </div>
              )}
            </div>
            {selectedBorrowAPY !== undefined && (
              <span className="text-muted-foreground text-sm">
                APY <span className="ml-1 font-medium text-foreground">{formatPercent(selectedBorrowAPY)}</span>
              </span>
            )}
          </div>
          <div className="flex rounded-lg border border-border bg-panel">
            <TokenSelectorDialog
              options={enhancedBorrowOptions}
              selectedUnderlying={displayedBorrowUnderlying}
              onSelect={handleBorrowSelect}
              title="Select borrow token"
              disabled={!selectedCollateralUnderlying}
              sortByLiquidity
            />
            <div className="flex flex-1 items-center justify-between px-4">
              {/* Left: Amount + USD */}
              <div className="flex flex-col">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={borrowAmount ?? ''}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized !== null) {
                      onBorrowAmountChange?.(normalized)
                    }
                  }}
                  className={cn(
                    'no-scrollbar w-full overflow-scroll bg-transparent text-white text-xl outline-none placeholder:text-white/30',
                    disableBorrowInput && 'cursor-not-allowed opacity-50',
                  )}
                  disabled={!selectedBorrowUnderlying || disableBorrowInput}
                />
                <span className="no-scrollbar max-w-12 overflow-scroll text-muted-foreground text-xs">
                  {USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(borrowUsdValue ?? 0), { compact: true })}
                </span>
              </div>
              {/* Right: MAX above Available */}
              <div className="flex flex-col items-end pl-1">
                {maxBorrowAmount && Number(maxBorrowAmount) > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => onBorrowAmountChange?.(maxBorrowAmount)}
                      disabled={disableBorrowInput || !selectedBorrowUnderlying}
                      className={cn(
                        'font-medium text-primary text-xs',
                        disableBorrowInput || !selectedBorrowUnderlying
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:text-primary/80',
                      )}
                    >
                      MAX
                    </button>
                    <span className="no-scrollbar max-w-12 overflow-scroll text-muted-foreground text-xs">
                      {USD_MOCK_TOKEN.format(NormalizedUnitNumber(maxBorrowAmount), { style: 'compact' })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Routes Table */}
      {routes.length === 0 ? (
        <Panel.Wrapper className="flex items-center justify-center p-8">
          <Typography variant="p" className="text-muted-foreground">
            {selectedCollateralUnderlying ? 'Select a borrow asset to see routes.' : 'Select collateral to start.'}
          </Typography>
        </Panel.Wrapper>
      ) : (
        <Panel.Wrapper className="overflow-hidden p-0">
          {routeAutoAdjusted && (
            <div className="flex items-center gap-2 border-amber-500/20 border-b bg-amber-500/5 px-4 py-2 md:px-6">
              <span className="text-amber-400 text-xs">
                Route adjusted for sufficient liquidity based on your borrow amount
              </span>
            </div>
          )}
          {/* Table Header */}
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-2 border-white/4 border-b bg-white/[.02] px-4 py-3 md:gap-4 md:px-6">
            <span className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Route</span>
            <span className="text-right font-medium text-muted-foreground text-xs uppercase tracking-wider">APY</span>
            <span className="text-right font-medium text-muted-foreground text-xs uppercase tracking-wider">LTV</span>
            <span className="text-right font-medium text-muted-foreground text-xs uppercase tracking-wider">
              Liquidity
            </span>
            <span className="w-6" />
          </div>

          {/* Table Body */}
          <div>
            {routes.map((route, idx) => {
              const key = `${route.collateral}-${route.liability}`
              const selected = selectedRouteKey === key
              const isBest = idx === 0
              const routePositions = routePositionsMap.get(key) || []
              const hasPositions = routePositions.length > 0
              // Check if user has any existing position borrowing from this liability vault
              const hasExistingPositionForLiability = liabilitiesWithExistingPositions.has(
                route.liability.toLowerCase(),
              )
              // "Create new position" is available if user has matching positions for this route
              // (they may want to keep the existing position for a different route) or already has
              // debt in this liability vault (they may want a separate position for the same borrow asset)
              const canCreateNewPosition = hasPositions || hasExistingPositionForLiability

              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    onSelectRoute(key)
                    // Auto-select first position if exists, otherwise 'new' only if allowed
                    if (hasPositions && routePositions?.[0]) {
                      onSelectPosition?.(routePositions[0].subAccountId)
                    } else if (canCreateNewPosition) {
                      onSelectPosition?.('new')
                    } else {
                      // No existing positions and can't create new - clear selection
                      onSelectPosition?.(undefined as unknown as number | 'new')
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectRoute(key)
                      if (hasPositions && routePositions?.[0]) {
                        onSelectPosition?.(routePositions[0].subAccountId)
                      } else if (canCreateNewPosition) {
                        onSelectPosition?.('new')
                      } else {
                        onSelectPosition?.(undefined as unknown as number | 'new')
                      }
                    }
                  }}
                  className={cn(
                    'flex w-full cursor-pointer flex-col border-white/4 border-b px-4 text-left transition-colors md:px-6',
                    selected ? 'bg-primary/5' : 'hover:bg-white/[.02]',
                  )}
                >
                  {/* Main Row */}
                  <div className="grid w-full grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-1 py-3 lg:gap-4">
                    {/* Route Cell */}
                    <div className="flex flex-col gap-3">
                      <div className="-space-x-2 flex">
                        <TokenImage
                          src={getTokenImageUrl(route.collateralTokenInfo, route.collateralSymbol || '')}
                          alt=""
                          width={28}
                          height={28}
                          className="z-10 rounded-full ring-2 ring-background"
                          tokenAddress={route.collateralTokenInfo?.address}
                        />
                        <TokenImage
                          src={getTokenImageUrl(route.liabilityTokenInfo, route.liabilitySymbol || '')}
                          alt=""
                          width={28}
                          height={28}
                          className="rounded-full ring-2 ring-background"
                          tokenAddress={route.liabilityTokenInfo?.address}
                        />
                      </div>
                      <div className="flex flex-col gap-1 lg:gap-0">
                        <div className="flex flex-col items-start gap-1 lg:flex-row lg:items-center lg:gap-2">
                          <span className="flex items-center gap-1 font-semibold text-foreground">
                            {truncateWithEllipsis(route.collateralSymbol ?? '', 8)}
                            <span className="-mt-1 block text-dim-foreground">→</span>
                            {truncateWithEllipsis(route.liabilitySymbol ?? '', 8)}
                          </span>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            {route.clusterName && (
                              <Typography variant="prompt" className="text-muted-foreground lg:hidden">
                                {route.clusterName}
                              </Typography>
                            )}
                            <div className="flex items-center gap-2">
                              {isBest && (
                                <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-[10px] text-primary">
                                  Best
                                </span>
                              )}
                              {/* Euler positions badge */}
                              {hasPositions && route.routeType !== 'pooled' && (
                                <span className="whitespace-nowrap rounded bg-emerald-500/10 px-1.5 py-0.5 font-medium text-[10px] text-emerald-400">
                                  {routePositions.length} Position{routePositions.length > 1 ? 's' : ''}
                                </span>
                              )}
                              {/* Pooled position badge - single position per address */}
                              {route.routeType === 'pooled' && route.hasPosition && (
                                <span className="whitespace-nowrap rounded bg-emerald-500/10 px-1.5 py-0.5 font-medium text-[10px] text-emerald-400">
                                  Existing Position
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {route.clusterName && (
                          <Typography variant="prompt" className="hidden text-muted-foreground lg:inline">
                            {route.clusterName}
                          </Typography>
                        )}
                      </div>
                    </div>

                    {/* Borrow APY Cell */}
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-foreground">{formatPercent(route.borrowAPY)}</span>
                    </div>

                    {/* Max LTV Cell */}
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-foreground">{formatLtv(route.maxLTV)}</span>
                    </div>

                    {/* Liquidity Cell */}
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-foreground">{formatUsd(route.liquidityUSD)}</span>
                    </div>

                    {/* Selection Indicator */}
                    <div className="flex justify-end">
                      {selected ? (
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      ) : (
                        <div className="h-5 w-5 rounded-full border border-white/10" />
                      )}
                    </div>
                  </div>

                  {/* Inline Position Display with Radio Selection - only for Euler routes with positions */}
                  {selected && route.routeType !== 'pooled' && (hasPositions || canCreateNewPosition) && (
                    <div className="mb-3 space-y-2">
                      {/* Existing positions - only shown if there are matching positions */}
                      {hasPositions &&
                        routePositions.map((pos) => {
                          const isSelected = selectedPositionId === pos.subAccountId
                          return (
                            <button
                              key={pos.subAccountId}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                onSelectPosition?.(pos.subAccountId)
                              }}
                              className={cn(
                                'flex w-full flex-col items-start gap-3 rounded-lg border px-4 py-2 text-left transition-colors',
                                isSelected
                                  ? 'border-primary/40 bg-primary/10'
                                  : 'border-emerald-500/20 bg-emerald-500/5 hover:border-emerald-500/40',
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {/* Radio Button */}
                                <div
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                    isSelected ? 'border-primary bg-primary' : 'border-white/30',
                                  )}
                                >
                                  {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                                </div>

                                {/* Position Label */}
                                <span className="shrink-0 rounded bg-emerald-500/20 px-1.5 py-0.5 font-semibold text-[10px] text-emerald-400 uppercase tracking-wide">
                                  Existing Position
                                </span>
                                {/* <div className="hidden h-4 w-px shrink-0 bg-emerald-500/20 lg:block" /> */}
                              </div>

                              {/* Lend: All Collaterals */}
                              <div className="flex gap-2">
                                <span className="w-11 shrink-0 pt-0.5 font-medium text-emerald-400 text-xs lg:pt-0">
                                  Lend:
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                  {pos.collaterals.map((c) => (
                                    <div key={c.vaultAddress} className="flex shrink-0 items-center gap-1.5">
                                      <TokenIcon
                                        token={{
                                          logoURI: getLogoForVault(c.vaultAddress),
                                          symbol: TokenSymbol(c.symbol),
                                        }}
                                        className="h-4 w-4"
                                      />
                                      <span className="text-foreground text-sm">
                                        {Number(formatUnits(c.assets, c.decimals)).toFixed(4)} {c.symbol}
                                      </span>
                                      <span className="text-muted-foreground text-xs">${c.valueUsd.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Borrow: Borrows */}
                              {pos.borrows.length > 0 && (
                                <>
                                  {/* <div className="hidden h-4 w-px shrink-0 bg-white/10 lg:block" /> */}
                                  <div className="flex items-center gap-2">
                                    <span className="w-11 shrink-0 font-medium text-red-400 text-xs lg:w-auto">
                                      Borrow:
                                    </span>
                                    {pos.borrows.map((b) => (
                                      <div key={b.vaultAddress} className="flex items-center gap-1.5">
                                        <TokenIcon
                                          token={{
                                            logoURI: getLogoForVault(b.vaultAddress),
                                            symbol: TokenSymbol(b.symbol),
                                          }}
                                          className="h-4 w-4"
                                        />

                                        <span className="text-red-400 text-sm">
                                          -{Number(formatUnits(b.debt, b.decimals)).toFixed(4)} {b.symbol}
                                        </span>
                                        <span className="text-muted-foreground text-xs">${b.debtUsd.toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </button>
                          )
                        })}

                      {/* Create New Position Option - only shown when user has existing position with this liability */}
                      {canCreateNewPosition && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onSelectPosition?.('new')
                          }}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg border px-4 py-2 text-left transition-colors',
                            selectedPositionId === 'new'
                              ? 'border-primary/40 bg-primary/10'
                              : 'border-white/10 bg-white/[.02] hover:border-white/20',
                          )}
                        >
                          {/* Radio Button */}
                          <div
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              selectedPositionId === 'new' ? 'border-primary bg-primary' : 'border-white/30',
                            )}
                          >
                            {selectedPositionId === 'new' && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </div>
                          <span className="text-muted-foreground text-sm">Create a new position</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Pooled Position Display - show ALL user supplies and borrows (one unified position) */}
                  {selected && route.routeType === 'pooled' && userPooledPosition && (
                    <div className="mb-3">
                      <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 rounded bg-emerald-500/20 px-1.5 py-0.5 font-semibold text-[10px] text-emerald-400 uppercase tracking-wide">
                            Existing Position
                          </span>
                        </div>

                        {/* Show all user supplies */}
                        {userPooledPosition.supplies.length > 0 && (
                          <div className="flex gap-2">
                            <span className="w-11 shrink-0 pt-0.5 font-medium text-emerald-400 text-xs lg:pt-0">
                              Lend:
                            </span>
                            <div className="flex flex-wrap items-center gap-2">
                              {userPooledPosition.supplies.map((supply) => (
                                <div key={supply.symbol} className="flex items-center gap-1.5">
                                  <TokenIcon token={{ symbol: TokenSymbol(supply.symbol) }} className="h-4 w-4" />
                                  <span className="text-foreground text-sm">
                                    {supply.amount.toFixed(4)} {supply.symbol}
                                  </span>
                                  <span className="text-muted-foreground text-xs">${supply.amountUsd.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Show all user borrows */}
                        {userPooledPosition.borrows.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="w-11 shrink-0 font-medium text-red-400 text-xs">Borrow:</span>
                            <div className="flex flex-wrap items-center gap-2">
                              {userPooledPosition.borrows.map((borrow) => (
                                <div key={borrow.symbol} className="flex items-center gap-1.5">
                                  <TokenIcon token={{ symbol: TokenSymbol(borrow.symbol) }} className="h-4 w-4" />
                                  <span className="text-red-400 text-sm">
                                    -{borrow.amount.toFixed(4)} {borrow.symbol}
                                  </span>
                                  <span className="text-muted-foreground text-xs">${borrow.amountUsd.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Panel.Wrapper>
      )}
    </div>
  )
}
